import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { DeleteRuleDialog } from '../-components/DeleteRuleDialog';
import { EditRuleSheet } from '../-components/EditRuleSheet';
import { RulesTable } from '../-components/RulesTable';
import { adminRulesQueryOptions } from '../-hooks/useAdminRules';
import { useRulesPage } from '../-hooks/useRulesPage';

/**
 * No `search` key, unlike the other management lists: the route declares `q`
 * but `parseFilter` in `db/rules.js` never reads it, so there is no search box
 * whose term would need a home in the URL.
 */
const rulesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/rules/(pages)/')({
  validateSearch: rulesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject
  // hands the route to the router's error boundary, which hides the table's
  // own "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminRulesQueryOptions(deps))
      .catch(() => undefined),
  component: RulesPage,
});

function RulesPage() {
  const page = useRulesPage();

  return (
    <PageContainer>
      <RulesTable page={page} />
      <EditRuleSheet page={page} />
      <DeleteRuleDialog
        rule={page.pendingDeletion}
        onOpenChange={open => !open && page.cancelDeletion()}
        onConfirm={() => void page.confirmDeletion()}
        isPending={page.isPending}
      />
    </PageContainer>
  );
}
